import { isEmpty } from "lodash";
import dayjs from "dayjs";

import { BenefitPlanDetails, CheckEligibility, EligibilityCheck, Patient, PatientInformation, } from "../models/Patient";
import { formatDate } from "../utils/DateTime";
import { DateFormats } from "./Date";
import { Discipline, ListOptions } from "../models/Master";
import { DocumentReviewPayload, ReAuthRequestPayload, ReferralDetail } from "../models/Referral";
import { currentDate, getGender, getLanguageLabel, getLanguageValue, getValidMBI, getValue, splitMemberName } from "../utils";
import { physicianNameRegex } from "./Regex";
import { getDisciplinesFromReAuthServiceRequest, getFormattedCareType, getFormattedDisciplines, ReAuthServiceRequest } from "../utils/PdfGeneration";
import { MyAccountModel } from "../models/MyAccount";
import { ReAuthTabData, TabData } from "../models/TabSessionStorage";
import { subscriberDetails } from "./Patient";
import { matchState } from "../utils/StateSearch";

export const AuthorizationDetails = {
  requestId: "",
  userCreated: "",
  noteComment: "",
  dateCreated: "",
  authorizationDetail: {
    requestIntake: {
      referralDetail: {
        patientId: 0,
        receivedDateTime: "",
        urgencyId: 0,
        receivedMethodId: 0,
        receivedMethodName: "",
        isHomeBound: null,
        isSignedHealthOrder: "",
        signedOrderDate: "",
        signedOrderType: "",
        orderedById: null,
        acceptedById: null,
        isCustodialCare: "",
        isNonCoveredService: null,
        nonCoveredServices: null,
        healthProgramId: null,
        careProgramId: null,
        powerOfAttorney: null,
        urgencyStatus: null,
        orderedByName: null,
        acceptedByName: null,
        nonCoveredServiceNames: null,
        patientEligibilityId: 0,
        fullNameCreated: "",
        authorizationTypeId: 0,
        userCreated: "",
        userSpecificReceivedDateTime: "",
      },
      residentialAddress: {
        streetName1: null,
        streetName2: null,
        county: null,
        country: "",
        city: null,
        patientState: null,
        zipCode: null,
        latitude: null,
        longitude: null,
        addressTypeId: 1,
      },
      serviceAddress: {
        streetName1: null,
        streetName2: null,
        county: null,
        country: "",
        city: null,
        patientState: null,
        zipCode: null,
        latitude: null,
        longitude: null,
        addressTypeId: 2,
      },
      requester: {
        facilityTypeId: null,
        facilityName: "",
        requesterName: "",
        requesterExtension: "",
        requesterPhoneNo: "",
        requesterEmail: "",
        requesterFax: "",
        requesterNpi: "",
        streetName1: "",
        streetName2: "",
        county: null,
        city: "",
        requesterState: "",
        zipCode: "",
        facilityTypeName: "",
      },
      referralSource: {
        facilityTypeId: "",
        facilityTypeName: "",
        facilityName: "",
        referralSourceName: "",
        referralSourceExtension: null,
        referralSourcePhoneNo: "",
        referralSourceEmail: "",
        referralSourceFax: "",
        ReferralSourceNpi: "",
        streetName1: null,
        streetName2: null,
        county: null,
        city: null,
        referralSourceState: null,
        zipCode: null,
        sameAsRequestingFacility: false,
        dischargeDate: "",
      },
      isSameAsResidential: true,
    },
    icdCodesDetails: {
      typeId: 1,
      fullNameCreated: "",
      icdCodesInfoDto: [
        {
          diagnosisTypeId: 0,
          diagnosisCode: "",
          diagnosisCodeDescription: "",
          icdDiagnosisCodeId: 0,
        },
      ],
    },
    referralPhysicianDetail: {
      primaryPhysician: {
        referralPhysicianDetailId: 0,
        physicianId: 0,
        physicianName: "",
        physicianNpiId: "",
        phoneExt: null,
        phoneNo: "",
        taxId: null,
        streetName1: "",
        streetName2: null,
        fax: "",
        county: "",
        city: "",
        physicianState: "",
        zipCode: "",
        physicianTypeId: 0,
        userCreated: "",
        physicianAddress: null,
        fullNameCreated: "",
      },
      orderingPhysician: {
        referralPhysicianDetailId: 0,
        physicianId: 0,
        physicianName: "",
        physicianNpiId: "",
        phoneExt: null,
        phoneNo: "",
        taxId: null,
        streetName1: "",
        streetName2: null,
        fax: "",
        county: "",
        city: "",
        physicianState: "",
        zipCode: "",
        physicianTypeId: 0,
        physicianAddress: null,
      },
    },
    referralServiceProvider: {
      referralServiceProviderId: 0,
      serviceProviderId: "",
      providerName: "",
      providerNpi: "",
      paymentStructure: null,
      streetName1: null,
      streetName2: null,
      providerCounty: null,
      providerCity: null,
      providerState: null,
      zipCode: null,
      userCreated: "",
      fullNameCreated: "",
      reimbursementTypeId: 0,
      reimbursementTypeName: "",
    },
    serviceRequestDetails: {
      userCreated: "",
      startOfCareDate: "",
      referralCareTypeDto: [
        {
          careTypeId: "",
        },
      ],
      referralDisciplineDto: [
        {
          disciplineId: "",
          serviceNumber: 0,
          acceptedVisits: 0,
        },
      ],
      diseasesDto: null,
      psychologicalDiseasesDto: null,
      fullNameCreated: "",
      isNurseToOpen: false,
      userSpecificReceivedDateTime: "",
    },
  },
};

export interface ReAuthDiscipline {
  disciplineId: number;
  disciplineName: string;
  serviceNumber: number | null;
  currentRequestedVisit: number;
  nextRequestedVisit: number;
  totalRequestedVisit: number;
  endDateOfExistingDiscipline: string | null;
  currentStartDate: string;
  currentEndDate: string;
  nextStartDate: string;
  nextEndDate: string;
}
export interface ReAuthDetailsPayload {
  requestId: string;
  userCreated: string;
  referralId: number;
  noteComment: string;
  fullName: string;
  reAuthRequest: {
    referralId: number;
    patientEligibilityId: number;
    receivedMethodId: number;
    urgencyId: number;
    userNameCreated: string;
    userEmail: string;
    userPhone: string;
    facilityTypeId: number;
    userCreated: string;
    sendApprovedAuthTo: number;
    secondaryEmailAddress: string;
    requesterNpi: string;
  };
  followingPhysicianDetail: {
    serviceRequestId: number;
    referralId: number;
    physicianName: string;
    physicianNpiId: string;
    phoneNo: string;
    county: string;
    zipCode: string;
    userNameCreated: string;
    streetName1: string;
    streetName2: string;
    city: string;
    physicianState: string;
  };
  reAuthServiceRequest: {
    serviceRequestId: number;
    referralId: number;
    startOfCareDate: string;
    startOfCareVerified: boolean;
    currentStartDate: string;
    currentEndDate: string;
    nextStartDate: string;
    nextEndDate: string;
    userCreated: string;
    endDateOfExistingDisciplines: string;
    reAuthDiscipline: ReAuthDiscipline[];
    fullNameCreated: string;
    currentDate: string;
    highestCred: any;
  };
  icdCodesDetails: {
    typeId: number;
    fullNameCreated: string;
    icdCodesInfoDto: Array<{
      referralIcdCodeId: number;
      diagnosisTypeId: number;
      diagnosisCode: string;
      diagnosisCodeDescription: string;
      icdDiagnosisCodeId: number;
    }>;
  };
  authorizationNumber: string;
}

export const ReAuthDetails: ReAuthDetailsPayload = {
  requestId: "",
  userCreated: "",
  referralId: 0,
  noteComment: "",
  fullName: "",
  reAuthRequest: {
    referralId: 0,
    patientEligibilityId: 0,
    receivedMethodId: 0,
    urgencyId: 0,
    userNameCreated: "",
    userEmail: "",
    userPhone: "",
    facilityTypeId: 0,
    userCreated: "",
    sendApprovedAuthTo: 0,
    secondaryEmailAddress: "",
    requesterNpi: "",
  },
  followingPhysicianDetail: {
    serviceRequestId: 0,
    referralId: 0,
    physicianName: "",
    physicianNpiId: "",
    phoneNo: "",
    county: "",
    zipCode: "",
    userNameCreated: "",
    streetName1: "",
    streetName2: "",
    city: "",
    physicianState: "",
  },
  reAuthServiceRequest: {
    serviceRequestId: 0,
    referralId: 0,
    startOfCareDate: "",
    startOfCareVerified: false,
    currentStartDate: "",
    currentEndDate: "",
    nextStartDate: "",
    nextEndDate: "",
    userCreated: "",
    endDateOfExistingDisciplines: "",
    reAuthDiscipline: [],
    fullNameCreated: "",
    currentDate: "",
    highestCred: null,
  },
  icdCodesDetails: {
    typeId: 2,
    fullNameCreated: "",
    icdCodesInfoDto: [
      {
        referralIcdCodeId: 0,
        diagnosisTypeId: 0,
        diagnosisCode: "",
        diagnosisCodeDescription: "",
        icdDiagnosisCodeId: 0,
      },
    ],
  },
  authorizationNumber: "",
};

export const visitType = [
  "nextSN",
  "nextPT",
  "nextOT",
  "nextST",
  "nextMSW",
  "nextHHA",
  "homeHHA",
  "homeMSW",
  "homeST",
  "homeOT",
  "homePT",
  "homeSN",
];

export const visitTypeHome = [
  "homeHHA",
  "homeMSW",
  "homeST",
  "homeOT",
  "homePT",
  "homeSN",
];

export enum PostPriorAuthSubmitActionDispatchTypes {
  POST_PRIOR_AUTH_SUBMIT_SUCCESS = "POST_PRIOR_AUTH_SUBMIT_SUCCESS",
  POST_PRIOR_AUTH_SUBMIT_INVOKE = "POST_PRIOR_AUTH_SUBMIT_INVOKE",
  POST_PRIOR_AUTH_SUBMIT_ERRORS = "POST_PRIOR_AUTH_SUBMIT_ERRORS",
  POST_PRIOR_AUTH_SUBMIT_RESET = "POST_PRIOR_AUTH_SUBMIT_RESET",
}

export enum ReferralDocumentFileDispatchTypes {
  REFERRAL_DOCUMENT_SUCCESS = "REFERRAL_DOCUMENT_SUCCESS",
  REFERRAL_DOCUMENT_ERRORS = "REFERRAL_DOCUMENT_ERRORS",
  REFERRAL_DOCUMENT_INVOKE = "REFERRAL_DOCUMENT_INVOKE",
  REFERRAL_DOCUMENT_RESET = "REFERRAL_DOCUMENT_RESET",
}

export enum SlotsDispatchTypes {
  SLOTS_SUCCESS = "SLOTS_SUCCESS",
  SLOTS_ERRORS = "SLOTS_ERRORS",
  SLOTS_INVOKE = "SLOTS_INVOKE",
  SLOTS_RESET = "SLOTS_RESET",
}

export enum ReAuthDefaultReferralDataDispatchTypes {
  RE_AUTH_DEFAULT_REFERRAL_DATA_SUCCESS = "RE_AUTH_DEFAULT_REFERRAL_DATA_SUCCESS",
  RE_AUTH_DEFAULT_REFERRAL_DATA_INVOKE = "RE_AUTH_DEFAULT_REFERRAL_DATA_INVOKE",
  RE_AUTH_DEFAULT_REFERRAL_DATA_ERRORS = "RE_AUTH_DEFAULT_REFERRAL_DATA_ERRORS",
  RE_AUTH_DEFAULT_REFERRAL_DATA_RESET = "RE_AUTH_DEFAULT_REFERRAL_DATA_RESET",
}

export enum ReAuthDefaultServiceProviderDispatchTypes {
  RE_AUTH_DEFAULT_SERVICE_PROVIDER_SUCCESS = "RE_AUTH_DEFAULT_SERVICE_PROVIDER_SUCCESS",
  RE_AUTH_DEFAULT_SERVICE_PROVIDER_INVOKE = "RE_AUTH_DEFAULT_SERVICE_PROVIDER_INVOKE",
  RE_AUTH_DEFAULT_SERVICE_PROVIDER_ERRORS = "RE_AUTH_DEFAULT_SERVICE_PROVIDER_ERRORS",
  RE_AUTH_DEFAULT_SERVICE_PROVIDER_RESET = "RE_AUTH_DEFAULT_SERVICE_PROVIDER_RESET",
}

export enum PostReAuthRequestDispatchTypes {
  POST_RE_AUTH_REQUEST_SUCCESS = "POST_RE_AUTH_REQUEST_SUCCESS",
  POST_RE_AUTH_REQUEST_INVOKE = "POST_RE_AUTH_REQUEST_INVOKE",
  POST_RE_AUTH_REQUEST_ERRORS = "POST_RE_AUTH_REQUEST_ERRORS",
  POST_RE_AUTH_REQUEST_RESET = "POST_RE_AUTH_REQUEST_RESET",
}

/**Ingest Prior Auth Process */

export enum IngestProcessActionDispatchTypes {
  INGEST_PROCESS_SUCCESS = "INGEST_PROCESS_SUCCESS",
  INGEST_PROCESS_ERRORS = "INGEST_PROCESS_ERRORS",
  INGEST_PROCESS_INVOKE = "INGEST_PROCESS_INVOKE",
  INGEST_PROCESS_RESET = "INGEST_PROCESS_RESET",
}

export const getMatchingNames = (array1: any, array2: Discipline[] , type?: number) => {
  if (!Array.isArray(array1) || !Array.isArray(array2)) {
    return [];
  }
  return array2
    .filter((item2: any) =>
      array1.some((item1: any) => item2.disciplineId === (type === 1 ? item1 : item1.disciplineId))
    ) // Match by id
    .map((item: any) => item.disciplineName);
};

const getValidCareType = (data: any, masterCareTypeState: ListOptions[]) => {
    const careTypeId = data.authorizationDetail?.serviceRequestDetails
    ?.referralCareTypeDto[0].careTypeId;
    if (careTypeId > 0) {
      const filteredLabel = masterCareTypeState &&
      masterCareTypeState.filter(
        (item: ListOptions) =>
          item.value ===
          data.authorizationDetail?.serviceRequestDetails
            ?.referralCareTypeDto[0].careTypeId
        )[0].label
      return filteredLabel;
    }
    return null
}

type EligibilityCheckOrNull = EligibilityCheck | null | undefined;

interface SubmissionContentParams {
  data: any;
  values: any;
  eligibilityPayload: EligibilityCheckOrNull;
  memberInfoValues: any;
  masterCareTypeState: ListOptions[];
  documents: string[];
  disciplinesRecord: Discipline[];
  secondaryDiagnosis: any[];
  primaryDiagnosis: any;
  approvedAuthPreference: any;
  message: string;
}

export const getSubmissionContentForIngest = ({
  data,
  values,
  eligibilityPayload,
  memberInfoValues,
  masterCareTypeState,
  documents,
  disciplinesRecord,
  secondaryDiagnosis,
  primaryDiagnosis,
  approvedAuthPreference,
  message,
}: SubmissionContentParams) => {

  if (!isEmpty(eligibilityPayload?.lastName)) {
    return {
      EligibilityCheck: {
        LastName: eligibilityPayload?.lastName,
        DateOfBirth: eligibilityPayload?.dob
        ? formatDate(eligibilityPayload.dob, DateFormats.MM_DD_YYYY)
          : "",
        SubscriberId: eligibilityPayload?.policyId,
        PayerId: eligibilityPayload?.payerId,
        PayerName: eligibilityPayload?.payerName,
      },
      MemberDetails: {
        MemberName: memberInfoValues?.memberName,
        SubscriberId: memberInfoValues?.subscriberId,
        Address:
          memberInfoValues?.streetName1 +
          memberInfoValues?.city +
          memberInfoValues?.patientState +
          memberInfoValues?.country +
          memberInfoValues?.zipCode,
        PhoneNumber: memberInfoValues?.phoneNumber,
        DateOfBirth: memberInfoValues?.dateOfBirth,
        MBI: memberInfoValues?.mbi,
        MemberPreferredLanguage: getLanguageValue(memberInfoValues?.memberPreferredLanguage),
        Gender: memberInfoValues?.gender,
        HealthPlanName: memberInfoValues?.healthPlan,
      },
      AgencyDetails: {
        AgencyId:
          data.authorizationDetail?.referralServiceProvider.serviceProviderId,
        RequestorEmail: values().userCreated,
        AgencyName:
          data.authorizationDetail?.referralServiceProvider?.providerName,
        AgencyLocationAddress: getValue(data ,"authorizationDetail.referralServiceProvider.providerAddress"),
        AgencyPhone: data.authorizationDetail?.requestIntake?.requesterPhoneNo,
        Fax: data.authorizationDetail?.requestIntake?.requesterFax,
        ApprovedAuthPreference: approvedAuthPreference,
      },
      FacilityInformation: {
        Name: data.authorizationDetail?.requestIntake?.referralSource
          ?.referralSourceName,
        NPI: data.authorizationDetail?.requestIntake?.referralSource
          ?.ReferralSourceNpi,
        Contact:
          data.authorizationDetail?.requestIntake?.referralSource
            ?.referralSourceEmail,
        Phone:
          data.authorizationDetail?.requestIntake?.referralSource
            ?.referralSourcePhoneNo,
        Fax: data.authorizationDetail?.requestIntake?.referralSource
          ?.referralSourceFax,
      },
      PrimaryCarePhysician: {
        Name: data.authorizationDetail?.referralPhysicianDetail
          ?.primaryPhysician?.physicianName.replace(physicianNameRegex, ""),
        NPI: data.authorizationDetail?.referralPhysicianDetail?.primaryPhysician
          ?.physicianNpiId,
        Address:
          data.authorizationDetail?.referralPhysicianDetail?.primaryPhysician
            ?.streetName1 + " " +
          data.authorizationDetail?.referralPhysicianDetail?.primaryPhysician
            ?.streetName2 + " " +
          data.authorizationDetail?.referralPhysicianDetail?.primaryPhysician
            ?.city + " " +
          data.authorizationDetail?.referralPhysicianDetail?.primaryPhysician
            ?.physicianState + " " +
          data.authorizationDetail?.referralPhysicianDetail?.primaryPhysician
            ?.county + " " +
          data.authorizationDetail?.referralPhysicianDetail?.primaryPhysician
            ?.zipCode,
        Phone:
          data.authorizationDetail?.referralPhysicianDetail?.primaryPhysician
            ?.phoneNo,
        Fax: data.authorizationDetail?.referralPhysicianDetail?.primaryPhysician
          ?.fax,
      },
      OrderingPhysician: {
        OrderingPrescriberName:
          data.authorizationDetail?.referralPhysicianDetail?.orderingPhysician
            ?.physicianName.replace(physicianNameRegex, ""),
        OrderingPrescriberNPI:
          data.authorizationDetail?.referralPhysicianDetail?.orderingPhysician
            ?.physicianNpiId,
        Address:
          data.authorizationDetail?.referralPhysicianDetail?.orderingPhysician
            ?.streetName1 + " " +
          data.authorizationDetail?.referralPhysicianDetail?.orderingPhysician
            ?.streetName2 + " " +
          data.authorizationDetail?.referralPhysicianDetail?.orderingPhysician
            ?.city + " " +
          data.authorizationDetail?.referralPhysicianDetail?.orderingPhysician
            ?.physicianState + " " +
          data.authorizationDetail?.referralPhysicianDetail?.orderingPhysician
            ?.county + " " +
          data.authorizationDetail?.referralPhysicianDetail?.orderingPhysician
            ?.zipCode,
        Phone:
          data.authorizationDetail?.referralPhysicianDetail?.orderingPhysician
            ?.phoneNo,
        Fax: data.authorizationDetail?.referralPhysicianDetail
          ?.orderingPhysician?.fax,
      },
      ServiceAddress: data.physicianServiceAddress,
      SocDate: formatDate(
        data.authorizationDetail?.serviceRequestDetails?.startOfCareDate,
        DateFormats.MM_DD_YYYY
      ),
      DateOfDC: formatDate(
        data.authorizationDetail?.requestIntake?.referralSource?.dischargeDate,
        DateFormats.MM_DD_YYYY
      ),
      PrimaryDiagnosis: primaryDiagnosis,
      SecondaryDiagnosis: secondaryDiagnosis,
      ClinicalGrouping: data.clinicalGrouping,
      CareType: getValidCareType(data, masterCareTypeState),
      SkilledType: getMatchingNames(
        data.authorizationDetail?.serviceRequestDetails?.referralDisciplineDto,
        disciplinesRecord
      ),
      Documents: documents,
      ReferralSource: "referralSourceHospital",
      UploadedByUserEmail:
        data.authorizationDetail?.serviceRequestDetails?.userCreated,
      SubmittedBy:
        data.authorizationDetail?.referralServiceProvider?.fullNameCreated,
      SubmittedDate: data.dateCreated, 
      Message: !isEmpty(message) ? message : "",
    };
  }
  return null;
};

interface MemberInfo {
  address: string;
  city: string;
  country: string;
  dateOfBirth: string;
  gender: string;
  healthPlan: string;
  mbi: string;
  memberName: string;
  memberPreferredLanguage: null | string;
  patientState: string;
  phoneNumber: string;
  streetName2: string;
  subscriberId: string;
  zipCode: string;
}
export const getSubmissionContentForReAuth = (
  payload: ReAuthRequestPayload,
  memberInformation: MemberInfo,
  eligibilityPayload: EligibilityCheck | null | undefined,
  values: any
) => {
  return {
    ReferralId: payload.referralId,
    EligibilityCheck: {
      LastName: eligibilityPayload?.lastName,
      DateOfBirth: eligibilityPayload?.dob
      ? formatDate(eligibilityPayload.dob, DateFormats.MM_DD_YYYY)
        : "",
      SubscriberId: eligibilityPayload?.policyId,
      PayerName: eligibilityPayload?.payerName,
    },
    MemberDetails: {
      MemberName: memberInformation?.memberName,
      SubscriberId: memberInformation?.subscriberId,
      Address: memberInformation?.address,
      PhoneNumber: memberInformation?.phoneNumber,
      DateOfBirth: memberInformation?.dateOfBirth,
      MBI: memberInformation?.mbi,
      MemberPreferredLanguage: memberInformation?.memberPreferredLanguage,
      Gender: memberInformation?.gender,
      HealthPlanName: memberInformation?.healthPlan,
    },
    RequestDetails: {
      DateOfRequest: formatDate(payload.dateCreated, DateFormats.MM_DD_YYYY),
      StartOfCare: formatDate(
        payload.reAuthServiceRequest.startOfCareDate,
        DateFormats.MM_DD_YYYY
      ),
    },
    AgencyDetails: {
      AgencyName: values.agencyName,
      AgencyNPI: values.requesterNpi,
      AgencyPhone: values.requesterPhoneNo,
      AgencyFax: values.requesterFax,
      AgencyAuthPreference: "",
    },
    PatientClinicianInformation: {
      FirstName: values.firstName,
      LastName: values.lastName,
      DateOfBirth: values.dateOfbirth,
      Address: values.street,
    },
    OrderingPhysician: {
      Name: payload.followingPhysicianDetail.physicianName,
      NPI: payload.followingPhysicianDetail.physicianNpiId,
      Phone: payload.followingPhysicianDetail.phoneNo,
    },
    AdditionalVisits: {
      SN: payload.reAuthServiceRequest.homeSN,
      PT: payload.reAuthServiceRequest.homePT,
      OT: payload.reAuthServiceRequest.homeOT,
      ST: payload.reAuthServiceRequest.homeST,
      MSW: payload.reAuthServiceRequest.homeMSW,
      HHA: payload.reAuthServiceRequest.homeHHA,
    },
    UploadedByUserEmail: payload.userCreated,
    SubmittedBy: payload.reAuthRequest.userNameCreated,
    SubmittedDate: payload.dateCreated,
    NoteComment: payload.noteComment,
  };
};

export enum ReferralManagementActionDispatchTypes {
  GET_REFERRAL_MANAGEMENT_INVOKE = "GET_REFERRAL_MANAGEMENT_INVOKE",
  GET_REFERRAL_MANAGEMENT_SUCCESS = "GET_REFERRAL_MANAGEMENT_SUCCESS",
  GET_REFERRAL_MANAGEMENT_ERROR = "GET_REFERRAL_MANAGEMENT_ERROR",
}

export enum Constants {
  ALL = "all",
  ENGLISH = "English",
  SPANISH = "Spanish",
  StatusActive = "Active",
  RESET_PASSWORD = "ResetPassword",
  USER_REGISTRATION = "UserRegistration",
  FORGOT_PASSWORD = "isForgotPassword",
  TRUE = "true",
  CODE = "code",
  STATUS = "status",
  ENTER = "Enter",
  IcdDiagnosisType = "ReferralService/IcdDiagnosisType?searchText",
  UPLOAD_TRAINING_VIDEO_URL = "Training/uploadTrainingVideo",
  NURSE_TO_OPEN = "Nurse to open",
  SYSTEM_USER = "system@tangocare.com",
  INGEST_PROCESS_STATE_ERROR = "INGEST_PROCESS_STATE_ERROR",
  DOCUMENT_UPLOAD_URL = "ReferralService/AddPriorAuthorizationDocument",
  INJEST_PROCESS_URL = "Ingest/ProcessPayload",
  EMAIL = "Email",
  FAX = "Fax",
  SHOW_NOTIFICATION_PANEL = "Notifications/showNotifications",
  VIEWED_NOTIFICATION = "Notifications/markAsRead",
  DOWNLOAD_DOCUMENT_OF_NOTIFICATION = "Blob/GetDocumentBlobById"
}

export enum PayerIdInfo {
  AETNA_HEALTHPLAN_UID = "1a8fd082-60a3-488e-80ea-136fa1c342d2",
  BCBS_TN_HEALTHPLAN_UID = "d0f1b1fe-7fa3-47de-9f9c-df24738edeb9",
  BCBS_MICHIGAN_HEALTHPLAN_UID = "178da7dd-8465-4391-b252-0b2539fdc1e5",
}

export enum Numbers {
  ZERO = 0,
  ONE = 1,
  TWO = 2,
  THREE = 3,
  Four = 4,
  Eight = 8,
  Five = 5,  
}

/**
 * if value is null or undefined then return empty string
 * @param str 
 * @returns 
 */
type NullableString = string | null | undefined;

const getStringValue = (str: NullableString) => {
  if (str === null || str === undefined) {
    return "";
  }
  return str
}

/**
 * Return ContractId-PBP 
 * @param contractId 
 * @param pbp 
 * @returns 
 */
export const getContractIdPbp = (contractId: string | null | undefined, pbp: string | null | undefined) => {
  if (isEmpty(contractId) && isEmpty(pbp)) {
    return "";
  }
  return `${getStringValue(contractId)}-${getStringValue(pbp)}`;
};

/**
 * Function used to prepare information service payload
 * @param patientFromMbiState
 * @param checkEligibilityState
 * @param patientEligibilityStatus
 * @param eligibilityPayload
 * @returns
 */
interface PayloadParams {
  checkEligibilityState: CheckEligibility;
  patientEligibilityStatus: string;
  eligibilityPayload: EligibilityCheck | null | undefined;
  patientFromMbiState?: Patient | null;
  reAuthDefaultValueState?: ReferralDetail | null;
  benefitPlanUidState?: BenefitPlanDetails;
  patientInformationByIdState?: PatientInformation;
}

export const getPayloadForInformation = ({
  checkEligibilityState,
  patientEligibilityStatus,
  eligibilityPayload,
  patientFromMbiState,
  reAuthDefaultValueState,
  benefitPlanUidState,
  patientInformationByIdState,
}: PayloadParams) => {
  const getPatientId = () => {
    const patientIdFromMbi =
      patientFromMbiState?.patientDetails &&
      !isEmpty(patientFromMbiState?.patientDetails[0]) &&
      patientFromMbiState?.patientDetails[0].patientId > 0
        ? patientFromMbiState?.patientDetails[0].patientId
        : 0;
    return reAuthDefaultValueState ? reAuthDefaultValueState.patientId : patientIdFromMbi;
  };


  const getSubscriberDetails = () => {
    const { subscriberSuffix, groupPlanId, relationshipTypeId } = subscriberDetails(checkEligibilityState);
    return { subscriberSuffix, groupPlanId, relationshipTypeId };
  };

  const formatSubscriberDateOfBirth = () =>
    formatDate(
      getValue(checkEligibilityState, "supplementalInformation.info[0].userDefined11"),
      DateFormats.YYYY_MM_DD,
      DateFormats.YYYYMMDD
    ) || null;

  const buildAddress = (address: any, addressTypeId: number) => ({
    streetName1: address?.streetName1 ?? "",
    streetName2: address?.streetName2 ?? "",
    county: address?.county ?? "",
    city: address?.city ?? "",
    patientState: address?.patientState ?? "",
    zipCode: address?.zipCode ?? "",
    latitude: null,
    longitude: null,
    country: address?.country ?? "",
    addressTypeId,
  });

  const patientResidentialAddress = buildAddress(
    patientInformationByIdState?.addressInfo.patientResidentialAddress,
    1
  );

  const patientMailingAddress = buildAddress(
    patientInformationByIdState?.addressInfo.patientMailingAddress,
    3
  );

  const informationReceived = {
    patientId: getPatientId(),
    healthPlanUid: checkEligibilityState?.additionalNotes?.healthPlanUid ?? "",
    product: benefitPlanUidState?.product ?? "",
    benefitPlanUid: benefitPlanUidState?.benefitPlanUid ?? "",
    medicareBeneficiaryIdentifier: getValidMBI(
      patientInformationByIdState?.memberInfo.medicareBeneficiaryIdentifier ?? ""
    ),
    ahcccsId: null,
    eligibilityStatus: patientEligibilityStatus,
    effectiveDate: checkEligibilityState?.supplementalInformation?.info[0]?.healthCoverage?.beginDate,
    effectiveThrough: checkEligibilityState?.supplementalInformation?.info[0]?.healthCoverage?.endDate,
    subscriberId: patientInformationByIdState?.memberInfo.subscriberId ?? "",
    eligibilityPayerId: getValue(eligibilityPayload, "payerId"),
    eligibilityPayerName: getValue(eligibilityPayload, "payerName"),
    apiEligibilityId: checkEligibilityState?.additionalNotes?.apiRequestId,
    isManual: false,
    alternatePhone: null,
    healthPlan: benefitPlanUidState?.healthPlan ?? "",
    benefitPlanName: benefitPlanUidState?.benefitPlanName ?? "",
    healthPlanState: benefitPlanUidState?.healthPlanState ?? "",
    accommodationNames: checkEligibilityState?.supplementalInformation?.info[0]?.userDefined5,
    contractIdPbp: getContractIdPbp(
      checkEligibilityState?.supplementalInformation?.info[0]?.userDefined3,
      checkEligibilityState?.supplementalInformation?.info[0]?.userDefined4
    ),
    languagePreferenceValue: patientInformationByIdState?.memberInfo.languageId ?? "",
    gender:
      getGender(getValue(patientInformationByIdState, "memberInfo.gender")) ||
      getGender(getValue(checkEligibilityState, "record.subscriberGender")),
    phoneNo: getValue(patientInformationByIdState, "memberInfo.phoneNo"),
    ...getSubscriberDetails(),
    subscriberPrefix: getValue(checkEligibilityState, "supplementalInformation.info[0].userDefined8", ""),
    subscriberFirstName: getValue(checkEligibilityState, "supplementalInformation.info[0].userDefined9", ""),
    subscriberLastName: getValue(checkEligibilityState, "supplementalInformation.info[0].userDefined10", ""),
    subscriberDateOfBirth: formatSubscriberDateOfBirth(),
    subscriberGender: getGender(getValue(checkEligibilityState, "supplementalInformation.info[0].userDefined12", "")),
  };

  const informationPayload = {
    informationReceived,
    patientMailingAddress: isEmpty(patientMailingAddress?.zipCode) || isEmpty(patientMailingAddress?.patientState)
      ? { ...patientResidentialAddress, addressTypeId: 3 }
      : patientMailingAddress,
    patientResidentialAddress: isEmpty(patientResidentialAddress?.zipCode) || isEmpty(patientResidentialAddress?.patientState)
      ? { ...patientMailingAddress, addressTypeId: 1 }
      : patientResidentialAddress,
  };

  return informationPayload;
};

export const AuthorizationDefaultValuesForIngest = {
  referralDetail: {
    referralId: 0,
    patientId: 0,
    receivedDateTime: "",
    urgencyId: 0,
    receivedMethodId: 0,
    receivedMethodName: "",
    isSignedHealthOrder: "",
    signedOrderDate: "",
    signedOrderType: "",
    orderedById: null,
    acceptedById: null,
    isCustodialCare: "",
    isNonCoveredService: null,
    nonCoveredServices: null,
    healthProgramId: null,
    careProgramId: null,
    urgencyStatus: null,
    orderedByName: null,
    acceptedByName: null,
    nonCoveredServiceNames: null,
    patientEligibilityId: 0,
    fullNameCreated: "",
    authorizationNumber: null,
    authorizationTypeId: 0,
    authorizationTypeName: null,
    referralManagementSystemId: 0,
    referralManagementSystemName: null,
    userCreated: "",
    dateCreated: "",
    startOfCareDate: ""
  },
  patient: {
    patientId: 0,
    referralId: 0,
    blobId: 0,
    firstName: "",
    middleName: "",
    lastName: "",
    gender: "",
    dateOfBirth: "",
    dateCreated: "",
    emailAddress: "",
    phoneNo: "",
    extension: "",
    blobFileName: "",
    languageName: null,
    languageId: null,
  },
  patientEligibility: {
    patientId: 0,
    apiEligibilityId: 0,
    eligibilityPayerId: "",
    eligibilityPayerName: "",
    healthPlanId: 0,
    benefitPlanId: 0,
    healthPlan: "",
    product: "",
    benefitPlan: "",
    planName: null,
    planCoverage: null,
    contractId: null,
    groupPlanId: null,
    groupName: null,
    pmgCode: null,
    eligibilityStatus: "",
    effectiveDate: "",
    effectiveThrough: "",
    subscriberId: "",
    medicareBeneficiaryIdentifier: "",
    medicaidId: "",
    isManual: false,
    fullNameCreated: "",
  },
  emergencyContact: [],
  address: [
    {
      streetName1: "",
      streetName2: "",
      county: "",
      city: "",
      patientState: "",
      zipCode: "",
      latitude: 0,
      longitude: 0,
      addressTypeId: 0,
      country: "",
    },
  ],
  referralSource: {
    facilityTypeId: 0,
    referralSourceName: "",
    referralSourceExtension: "",
    referralSourcePhoneNo: "",
    referralSourceEmail: "",
    referralSourceFax: "",
    referralSourceNpi: "",
    streetName1: "",
    streetName2: "",
    county: "",
    city: "",
    referralSourceState: "",
    zipCode: "",
    sameAsRequestingFacility: false,
    dischargeDate: "",
    facilityTypeName: "",
    facilityName: "",
  },
  requester: {
    facilityTypeId: 0,
    requesterName: "",
    requesterExtension: "",
    requesterPhoneNo: "",
    requesterEmail: "",
    requesterFax: "",
    requesterNpi: "",
    streetName1: "",
    streetName2: "",
    county: "",
    city: "",
    requesterState: "",
    zipCode: "",
    facilityTypeName: "",
    facilityName: "",
    sendApprovedAuthTo: 0,
    secondaryEmailAddress: "",
  },
  icdCode: [
    {
      referralIcdCodeId: "",
      diagnosisTypeId: 0,
      diagnosisCode: "",
      diagnosisCodeDescription: "",
      icdDiagnosisCodeId: 0,
    },
  ],
  physicianDetails: [
    {
      referralId: 0,
      referralPhysicianDetailId: 0,
      physicianId: 0,
      physicianName: "",
      physicianNpiId: "",
      phoneExt: "",
      phoneNo: "",
      taxId: null,
      streetName1: "",
      streetName2: "",
      fax: "",
      county: "",
      city: "",
      physicianState: "",
      zipCode: "",
      physicianTypeId: 0,
      physicianAddress: "",
      fullNameCreated: "",
      latitude: "",
      longitude: "",
    },
  ],
  serviceProvider: {
    serviceProviderId: 0,
    parentCompany: 0,
    symplrId: 0,
    serviceProviderUid: "",
    providerName: "",
    ahcccsId: "",
    medicareId: "",
    providerNpi: "",
    streetName1: "",
    streetName2: "",
    zipCode: "",
    providerCity: "",
    providerState: "",
    providerCounty: "-", //tbd
    country: "-",  //tbd
    phone: "",
    extension: "",
    email: "",
    fax: "",
    contractEffectiveFrom: "",
    contractEffectiveTo: "",
    plexisOfficeId: "",
    providerId: 0,
    taxId: 0,
    userCreated: "",
    providerAddress: "",
    reimbursementTypeId: "",
    reimbursementTypeName: "",
    healthPlanId: "",
  },
  discipline: [
    {
      disciplineId: "",
      disciplineName: "",
      disciplineDescription: "",
      azCompeleteCheck: null,
    },
  ],
  careTypes: [
    {
      careTypeId: "",
      careTypeName: "",
    },
  ],
  blobDocument: null,
};

interface AuthorizationValuesParams {
  data: TabData;
  icdCodes: any;
  memberInfoValues: any;
  serviceProviderUid: any;
  disciplinesRecord: Discipline[];
  masterCareTypeState: ListOptions[];
  loggedInUser: MyAccountModel;
  transDate: string;
}

export const AuthorizationValuesForIngest = ({
  data,
  icdCodes,
  memberInfoValues,
  serviceProviderUid,
  disciplinesRecord,
  masterCareTypeState,
  loggedInUser,
  transDate,
}: AuthorizationValuesParams) => {
  const { firstName, lastName } = splitMemberName(memberInfoValues?.memberName); 

  return {
    ...AuthorizationDefaultValuesForIngest,
    referralDetail: {
      ...AuthorizationDefaultValuesForIngest.referralDetail,
      fullNameCreated: `${getValue(loggedInUser, "providerPortalUser.lastName")} ${getValue(loggedInUser, "providerPortalUser.firstName")}`,
      receivedDateTime: transDate,
      userCreated: data.requestService.userCreated,
      dateCreated: transDate,
      startOfCareDate: dayjs(data.requestService.startOfCare).format("MM/DD/YYYY"),
    },

    patient: {
      ...AuthorizationDefaultValuesForIngest.patient,
      firstName: firstName,
      lastName: lastName, 
      gender: memberInfoValues?.gender,
      dateOfBirth: memberInfoValues?.dob,
      dateCreated: transDate,
      phoneNo: memberInfoValues?.phoneNumber,
      languageName: getLanguageLabel(memberInfoValues?.memberPreferredLanguage),
      languageId: memberInfoValues?.memberPreferredLanguage,
    },

    patientEligibility: {
      ...AuthorizationDefaultValuesForIngest.patientEligibility,
      healthPlan: memberInfoValues?.healthPlan,
      product: memberInfoValues?.product,
      benefitPlan: memberInfoValues?.benefitPlanName,
      subscriberId: memberInfoValues?.subscriberId,
      medicareBeneficiaryIdentifier: memberInfoValues?.mbi,
      fullNameCreated: `${getValue(loggedInUser, "providerPortalUser.lastName")} ${getValue(loggedInUser, "providerPortalUser.firstName")}`,
    },

    address: [
      {
        ...AuthorizationDefaultValuesForIngest.address[0],
        streetName1: data.referralSource.serviceAddressDetails.streetName1,
        streetName2: data.referralSource.serviceAddressDetails.streetName2,
        county: data.referralSource.serviceAddressDetails.county,
        city: data.referralSource.serviceAddressDetails.city,
        patientState: matchState(data.referralSource.serviceAddressDetails.patientState),
        zipCode: data.referralSource.serviceAddressDetails.zipCode,
        addressTypeId: data.referralSource.serviceAddressDetails.addressTypeId,
        country: data.referralSource.serviceAddressDetails.country,
      },
    ],

    referralSource: {
      ...AuthorizationDefaultValuesForIngest.referralSource,
      facilityTypeId: data.referralSource.facilityTypeId,
      referralSourceName: data.referralSource.referralSourceName,
      referralSourcePhoneNo: data.referralSource.referralSourcePhoneNo,
      referralSourceEmail: data.referralSource.referralSourceEmail,
      referralSourceFax: data.referralSource.referralSourceFax,
      referralSourceNpi: data.referralSource.referralSourceNpi,
      facilityTypeName: data.referralSource.facilityTypeName,
      facilityName: data.referralSource.facilityName,
      referralSourceExtension: data.referralSource.referralSourceExtension,
      IsDischarged: data.referralSource.IsDischarged,
      dischargeDate: data.referralSource.dischargeDate
        ? formatDate(data.referralSource.dischargeDate, DateFormats.YYYY_MM_DD, null, true)
        : null,
    },

    requester: {
      ...AuthorizationDefaultValuesForIngest.requester,
      facilityName: data.agencyDetails.facilityName,
      requesterName: data.agencyDetails.requesterName,
      requesterPhoneNo: data.agencyDetails.requesterPhoneNo,
      requesterEmail: data.agencyDetails.requesterEmail,
      requesterFax: data.agencyDetails.requesterFax,
      requesterNpi: data.agencyDetails.requesterNpi,
      streetName1: data.agencyDetails.streetName1,
      streetName2: data.agencyDetails.streetName2,
      city: data.agencyDetails.city,
      requesterState: data.agencyDetails.requesterState,
      zipCode: data.agencyDetails.zipCode,
      sendApprovedAuthTo: data.agencyDetails.sendApprovedAuthTo,
      secondaryEmailAddress: data.agencyDetails.secondaryEmailAddress,
    },
    icdCode: icdCodes,
    physicianDetails: [
      data.physicianDetails.primaryPhysician,
      data.physicianDetails.orderingPhysician,
    ],
    serviceProvider: {
      ...AuthorizationDefaultValuesForIngest.serviceProvider,
      serviceProviderUid: serviceProviderUid,
      contractEffectiveFrom: currentDate(true),
      providerName: data.referralServiceProvider?.providerName,
      providerNpi: data.referralServiceProvider?.providerNpi,
      streetName1: data.referralServiceProvider?.streetName1,
      providerCity: data.referralServiceProvider.providerCity,
      providerState: data.referralServiceProvider.providerState,
      userCreated: data.requestService.userCreated,
    },
    discipline: getFormattedDisciplines(
      data.requestService.discipline,
      disciplinesRecord
    ),
    careTypes: getFormattedCareType(data, masterCareTypeState, 1),
  };
};

export const ReAuthorizationDefaultValuesForIngest = {
  referralDetail: {
    referralId: 0,
    patientId: 0,
    receivedDateTime: "",
    urgencyId: 0,
    receivedMethodId: 0,
    receivedMethodName: "",
    isSignedHealthOrder: "",
    signedOrderDate: "",
    signedOrderType: "",
    orderedById: null,
    acceptedById: null,
    isCustodialCare: "",
    isNonCoveredService: null,
    nonCoveredServices: null,
    healthProgramId: null,
    careProgramId: null,
    urgencyStatus: null,
    orderedByName: null,
    acceptedByName: null,
    nonCoveredServiceNames: null,
    patientEligibilityId: 0,
    fullNameCreated: "",
    authorizationNumber: null,
    authorizationTypeId: 0,
    authorizationTypeName: null,
    referralManagementSystemId: 0,
    referralManagementSystemName: null,
    userCreated: "",
    dateCreated: "",
    startOfCareDate: "",
  },
  patient: {
    patientId: 0,
    referralId: 0,
    blobId: 0,
    firstName: "",
    middleName: "",
    lastName: "",
    gender: "",
    dateOfBirth: "",
    dateCreated: "",
    emailAddress: "",
    phoneNo: "",
    extension: "",
    blobFileName: "",
    languageName: null,
    languageId: null,
  },
  patientEligibility: {
    patientId: 0,
    apiEligibilityId: 0,
    eligibilityPayerId: 0,
    eligibilityPayerName: "",
    healthPlanId: 0,
    benefitPlanId: 0,
    healthPlan: "",
    product: "",
    benefitPlan: "",
    planName: null,
    planCoverage: null,
    contractId: null,
    groupPlanId: null,
    groupName: null,
    pmgCode: null,
    eligibilityStatus: "",
    effectiveDate: "",
    effectiveThrough: "",
    subscriberId: 0,
    medicareBeneficiaryIdentifier: "",
    medicaidId: 0,
    isManual: false,
    fullNameCreated: "",
  },
  emergencyContact: null,
  address: [
    {
      streetName1: "",
      streetName2: "",
      county: "",
      city: "",
      patientState: "",
      zipCode: "",
      latitude: 0,
      longitude: 0,
      addressTypeId: 0,
      country: "",
    },
  ],
  referralSource: {
    facilityTypeId: 0,
    referralSourceName: "",
    referralSourceExtension: "",
    referralSourcePhoneNo: "",
    referralSourceEmail: "",
    referralSourceFax: "",
    referralSourceNpi: "",
    streetName1: "",
    streetName2: "",
    county: "",
    city: "",
    referralSourceState: "",
    zipCode: "",
    sameAsRequestingFacility: false,
    dischargeDate: "",
    facilityTypeName: "",
    facilityName: "",
  },
  requester: {
    facilityTypeId: 0,
    requesterName: "",
    requesterExtension: "",
    requesterPhoneNo: "",
    requesterEmail: "",
    requesterFax: "",
    requesterNpi: "",
    streetName1: "",
    streetName2: "",
    county: "",
    city: "",
    requesterState: "",
    zipCode: "",
    facilityTypeName: "",
    facilityName: "",
    sendApprovedAuthTo: 0,
    secondaryEmailAddress: "",
  },
  icdCode: [
    {
      referralIcdCodeId: 0,
      diagnosisTypeId: 0,
      diagnosisCode: "",
      diagnosisCodeDescription: "",
      icdDiagnosisCodeId: 0,
    },
  ],
  physicianDetails: [
    {
      referralId: 0,
      referralPhysicianDetailId: 0,
      physicianId: 0,
      physicianName: "",
      physicianNpiId: 0,
      phoneExt: "",
      phoneNo: "",
      taxId: null,
      streetName1: "",
      streetName2: "",
      fax: "",
      county: "",
      city: "",
      physicianState: "",
      zipCode: "",
      physicianTypeId: 0,
      physicianAddress: "",
      fullNameCreated: "",
      latitude: "",
      longitude: "",
    },
  ],
  serviceProvider: {
    serviceProviderId: 0,
    parentCompany: 0,
    symplrId: 0,
    serviceProviderUid: "",
    providerName: "",
    ahcccsId: 0,
    medicareId: 0,
    providerNpi: "",
    streetName1: "",
    streetName2: "",
    zipCode: "",
    providerCity: "",
    providerState: "",
    providerCounty: "-", //tbd
    country: "-", //tbd
    phone: "",
    extension: "",
    email: "",
    fax: "",
    contractEffectiveFrom: "",
    contractEffectiveTo: null,
    plexisOfficeId: 0,
    providerId: 0,
    taxId: 0,
    userCreated: "",
    providerAddress: "",
    reimbursementTypeId: 0,
    reimbursementTypeName: "",
    healthPlanId: 0,
  },
  discipline: [
    {
      disciplineId: 0,
      disciplineName: "",
      disciplineDescription: "",
      azCompeleteCheck: null,
      requestedVisits: 0,
      effectiveFromDate: "",
      effectiveThroughDate: "",
      slotType: 0,
      effectiveDateRange: "",
      endDateOfExistingDiscipline: "",
    },
  ],
  careTypes: [
    {
      careTypeId: 0,
      careTypeName: "",
    },
  ],
  blobDocument: null,
  followingPhysician: [
    {
      referralId: 0,
      referralPhysicianDetailId: 0,
      physicianId: 0,
      physicianName: "",
      physicianNpiId: "",
      phoneExt: null,
      phoneNo: "",
      taxId: null,
      streetName1: "",
      streetName2: "",
      fax: "",
      county: "",
      city: "",
      physicianState: "",
      zipCode: "",
      followingPhysicianId: 0,
      physicianAddress: "",
      fullNameCreated: "",
      latitude: null,
      longitude: null,
    },
  ],
  notesDetails: {    
     referralId: 0,     
     comment: "",   
  }, 
};

export const ReAuthorizationValuesForIngest = (
  payload: ReAuthTabData,
  memberInformation: PatientInformation,
  requestUuid: string,
  loggedInUser: MyAccountModel,
  transDate: string
) => {
  return {
    ...ReAuthorizationDefaultValuesForIngest,
    referralDetail: {
      ...ReAuthorizationDefaultValuesForIngest.referralDetail,
      referralId: memberInformation.referralId,
      patientId: memberInformation.patientId,
      receivedDateTime: transDate,
      urgencyId: payload.reAuthRequest.urgencyId,
      receivedMethodId: payload.reAuthRequest.receivedMethodId,
      fullNameCreated: payload.reAuthRequest.userNameCreated, 
      userCreated: payload.icdCodesDetails.fullNameCreated,
      dateCreated: transDate,
      startOfCareDate: dayjs(payload.requestService.startOfCare).format("MM/DD/YYYY"),
    },
    patient: {
      ...ReAuthorizationDefaultValuesForIngest.patient,
      firstName: memberInformation.patientFirstName,
      lastName: memberInformation.patientLastName,
      gender: memberInformation.memberInfo.gender,
      dateOfBirth: memberInformation?.memberInfo.dateOfBirth,
      dateCreated: transDate,
      phoneNo: memberInformation.memberInfo.phoneNo,
      languageName: memberInformation.memberInfo.languageName,
    },
    patientEligibility: {
      ...ReAuthorizationDefaultValuesForIngest.patientEligibility,
      healthPlan: memberInformation.memberInfo.healthPlan,
      subscriberId:  memberInformation.memberInfo.subscriberId,
      medicareBeneficiaryIdentifier: memberInformation.memberInfo.medicareBeneficiaryIdentifier,
      fullNameCreated: `${getValue(loggedInUser, "providerPortalUser.lastName")} ${getValue(loggedInUser, "providerPortalUser.firstName")}`,
    },
    referralSource: {
      ...ReAuthorizationDefaultValuesForIngest.referralSource,
      referralSourcePhoneNo: payload.reAuthAgencyDetails.requesterPhoneNo,
      referralSourceEmail: payload.reAuthAgencyDetails.requesterEmail,
      referralSourceFax: payload.reAuthAgencyDetails.requesterFax,
      referralSourceNpi: payload.reAuthAgencyDetails.requesterNpi,
      streetName1: payload.reAuthAgencyDetails.streetName1,
      streetName2: payload.reAuthAgencyDetails.streetName2,
      county: payload.reAuthAgencyDetails.county,
      city: payload.reAuthAgencyDetails.city,
      referralSourceState: payload.reAuthAgencyDetails.requesterState,
      zipCode: payload.reAuthAgencyDetails.zipCode,
      facilityName: payload.reAuthAgencyDetails.facilityName,
  },
    requester: {
      ...ReAuthorizationDefaultValuesForIngest.requester,
      requesterName: payload.reAuthAgencyDetails.requesterName,
      requesterPhoneNo: payload.reAuthAgencyDetails.requesterPhoneNo,
      requesterEmail: payload.reAuthAgencyDetails.requesterEmail,
      requesterFax: payload.reAuthAgencyDetails.requesterFax,
      requesterNpi: payload.reAuthAgencyDetails.requesterNpi,
      sendApprovedAuthTo: payload.reAuthAgencyDetails.sendApprovedAuthTo,
      secondaryEmailAddress: payload.reAuthAgencyDetails.secondaryEmailAddress,
    },
    serviceProvider: {
      ...ReAuthorizationDefaultValuesForIngest.serviceProvider,
      serviceProviderUid: requestUuid,
      contractEffectiveFrom: currentDate(true),
    },
    discipline: getDisciplinesFromReAuthServiceRequest(payload.requestService as ReAuthServiceRequest),
    followingPhysician: [
      {
        ...ReAuthorizationDefaultValuesForIngest.followingPhysician[0],
        referralId: payload.followingPhysicianDetail.referralId,
        physicianName: payload.followingPhysicianDetail.physicianName,
        physicianNpiId: payload.followingPhysicianDetail.physicianNpiId,
        phoneNo: payload.followingPhysicianDetail.phoneNo,
        streetName1: payload.followingPhysicianDetail.streetName1,
        streetName2: payload.followingPhysicianDetail.streetName2,
        county: payload.followingPhysicianDetail.county,
        city: payload.followingPhysicianDetail.city,
        physicianState: payload.followingPhysicianDetail.physicianState,
        zipCode: payload.followingPhysicianDetail.zipCode,
        fax: payload.followingPhysicianDetail.fax,
      },
    ],
    notesDetails: {    
     referralId: payload.referralId,     
     comment: payload.noteComment,   
    },
  };
};
export enum DocumentReviewActionDispatchTypes {
  DOCUMENT_REVIEW_INVOKE = "DOCUMENT_REVIEW_INVOKE",
  DOCUMENT_REVIEW_SUCCESS = "DOCUMENT_REVIEW_SUCCESS",
  DOCUMENT_REVIEW_ERROR = "DOCUMENT_REVIEW_ERROR",
  DOCUMENT_REVIEW_RESET = "DOCUMENT_REVIEW_RESET"
}

export enum DocumentDownloadActionDispatchTypes {
  DOCUMENT_DOWNLOAD_ERROR = "DOCUMENT_DOWNLOAD_ERROR",
  DOCUMENT_DOWNLOAD_INVOKE = "DOCUMENT_DOWNLOAD_INVOKE",
  DOCUMENT_DOWNLOAD_RESET = "DOCUMENT_DOWNLOAD_RESET",
  DOCUMENT_DOWNLOAD_SUCCESS = "DOCUMENT_DOWNLOAD_SUCCESS",
}

export enum GetPhysicianDetailsActionDispatchTypes {
   GET_PHYSICIAN_DETAILS_ERROR = "GET_PHYSICIAN_DETAILS_ERROR",
   GET_PHYSICIAN_DETAILS_INVOKE = "GET_PHYSICIAN_DETAILS_INVOKE",
   GET_PHYSICIAN_DETAILS_RESET = "GET_PHYSICIAN_DETAILS_RESET",
   GET_PHYSICIAN_DETAILS_SUCCESS = "GET_PHYSICIAN_DETAILS_SUCCESS",
}

export const createDocumentReviewPayload = (
  referralId: number
): DocumentReviewPayload => ({
  referralId,
  pageNo: 1,
  pageSize: 999,
  type: 0,
  documentDateTime: {
    firstDateTime: undefined,
    secondDateTime: undefined,
    dateType: undefined,
    utcOffsetValue: undefined,
  },
  documentSource: [],
  documentType: [],
  documentCreatedBy: [],
  documentFaxStatus: [],
});

export const RequestTabCount = 6;
export const RequestReAuthTabCount = 5;
export const SECRET_KEY = "Portal_Key";
export const SALT = "Portal_Salt";
export const ReferralSourceStateId = 7;

export enum PriorAuthHeaderTitle { 
  AGENCY_DETAILS = "Agency Details",
  REFERRAL_SOURCE = "Referral Source",
  PHYSICIAN_DETAIL = "Physician Detail",
  ICD_10 = "ICD 10",
  REQUEST_SERVICES = "Request Services",
  DOCUMENT_UPLOAD = "Document Upload",
  REVIEW = "Review",
}

export const PriorAuthHeaderLabel = [
  { label: PriorAuthHeaderTitle.AGENCY_DETAILS, fontWeight: 500 },
  { label: PriorAuthHeaderTitle.REFERRAL_SOURCE, fontWeight: 500 },
  { label: PriorAuthHeaderTitle.PHYSICIAN_DETAIL, fontWeight: 500 },
  { label: PriorAuthHeaderTitle.ICD_10, fontWeight: 500 },
  { label: PriorAuthHeaderTitle.REQUEST_SERVICES, fontWeight: 500 },
  { label: PriorAuthHeaderTitle.DOCUMENT_UPLOAD, fontWeight: 500 },
  { label: PriorAuthHeaderTitle.REVIEW, fontWeight: 500 },
];

export const SaveDetailsAlertMessage = "Are you sure you want to save details?";
export interface ConfirmationMessageType {
  title: string;
  subTitle: string;
}
export const confirmationMessage = {
  title: "Authorization Request has been successfully sent",
  subTitle: "Request No:",
}

export const ReAuthHeaderLabel = [
  { label: PriorAuthHeaderTitle.AGENCY_DETAILS, fontWeight: 500 },
  { label: PriorAuthHeaderTitle.PHYSICIAN_DETAIL, fontWeight: 500 },
  { label: PriorAuthHeaderTitle.ICD_10, fontWeight: 500 },
  { label: PriorAuthHeaderTitle.REQUEST_SERVICES, fontWeight: 500 },
  { label: PriorAuthHeaderTitle.DOCUMENT_UPLOAD, fontWeight: 500 },
  { label: PriorAuthHeaderTitle.REVIEW, fontWeight: 500 },
];

export enum DisciplineTableHeader {
  SELECT = "Select",
  DISCIPLINES = "Disciplines",
  REQUESTED_NO_OF_VISITS = "Requested no. of visits",
  TOTAL = "TOTAL:",
  MEMBER_ADMITTED = "Has the member been admitted as an inpatient since your last request?",
  MEMBER_EMERGENCY = "Has the member had any trips to the emergency department since the last request?",

}

export enum GetDisciplineStatusListActionDispatchTypes {
  GET_DISCIPLINE_STATUS_LIST_ERROR = "GET_DISCIPLINE_STATUS_LIST_ERROR",
  GET_DISCIPLINE_STATUS_LIST_INVOKE = "GET_DISCIPLINE_STATUS_LIST_INVOKE",
  GET_DISCIPLINE_STATUS_LIST_RESET = "GET_DISCIPLINE_STATUS_LIST_RESET",
  GET_DISCIPLINE_STATUS_LIST_SUCCESS = "GET_DISCIPLINE_STATUS_LIST_SUCCESS",
}

export enum GetIsRetroDetailsActionDispatchTypes {
  GET_IS_RETRO_DETAILS_ERROR = "GET_IS_RETRO_DETAILS_ERROR",
  GET_IS_RETRO_DETAILS_INVOKE = "GET_IS_RETRO_DETAILS_INVOKE",
  GET_IS_RETRO_DETAILS_RESET = "GET_IS_RETRO_DETAILS_RESET",
  GET_IS_RETRO_DETAILS_SUCCESS = "GET_IS_RETRO_DETAILS_SUCCESS",
}

export enum NoMncDetailsActionDispatchTypes {
  NO_MNC_DOCUMENT_DETAILS_ERROR = "NO_MNC_DOCUMENT_DETAILS_ERROR",
  NO_MNC_DOCUMENT_DETAILS_INVOKE = "NO_MNC_DOCUMENT_DETAILS_INVOKE",
  NO_MNC_DOCUMENT_DETAILS_RESET = "NO_MNC_DOCUMENT_DETAILS_RESET",
  NO_MNC_DOCUMENT_DETAILS_SUCCESS = "NO_MNC_DOCUMENT_DETAILS_SUCCESS",
}

export enum UpdateStartOfCareDateActionDispatchTypes {
  UPDATE_START_OF_CARE_DATE_ERROR = "UPDATE_START_OF_CARE_DATE_ERROR",
  UPDATE_START_OF_CARE_DATE_INVOKE = "UPDATE_START_OF_CARE_DATE_INVOKE",
  UPDATE_START_OF_CARE_DATE_RESET = "UPDATE_START_OF_CARE_DATE_RESET",
  UPDATE_START_OF_CARE_DATE_SUCCESS = "UPDATE_START_OF_CARE_DATE_SUCCESS",
}

export enum GetNotificationsListActionDispatchTypes {
  GET_NOTIFICATIONS_LIST_ERROR = "GET_NOTIFICATIONS_LIST_ERROR",
  GET_NOTIFICATIONS_LIST_INVOKE = "GET_NOTIFICATIONS_LIST_INVOKE",
  GET_NOTIFICATIONS_LIST_RESET = "GET_NOTIFICATIONS_LIST_RESET",
  GET_NOTIFICATIONS_LIST_SUCCESS = "GET_NOTIFICATIONS_LIST_SUCCESS",
}

export enum UpdateNotificationViewListActionDispatchTypes {
  UPDATE_NOTIFICATION_VIEW_LIST_ERROR = "UPDATE_NOTIFICATION_VIEW_LIST_ERROR",
  UPDATE_NOTIFICATION_VIEW_LIST_INVOKE = "UPDATE_NOTIFICATION_VIEW_LIST_INVOKE",
  UPDATE_NOTIFICATION_VIEW_LIST_RESET = "UPDATE_NOTIFICATION_VIEW_LIST_RESET",
  UPDATE_NOTIFICATION_VIEW_LIST_SUCCESS = "UPDATE_NOTIFICATION_VIEW_LIST_SUCCESS",
}

export enum DownloadDocumentOfNotificationActionDispatchTypes {
  DOWNLOAD_DOCUMENT_OF_NOTIFICATION_ERROR = "DOWNLOAD_DOCUMENT_OF_NOTIFICATION_ERROR",
  DOWNLOAD_DOCUMENT_OF_NOTIFICATION_INVOKE = "DOWNLOAD_DOCUMENT_OF_NOTIFICATION_INVOKE",
  DOWNLOAD_DOCUMENT_OF_NOTIFICATION_RESET = "DOWNLOAD_DOCUMENT_OF_NOTIFICATION_RESET",
  DOWNLOAD_DOCUMENT_OF_NOTIFICATION_SUCCESS = "DOWNLOAD_DOCUMENT_OF_NOTIFICATION_SUCCESS",
  DOWNLOAD_DOCUMENT_OF_NOTIFICATION_PROGRESS = "DOWNLOAD_DOCUMENT_OF_NOTIFICATION_PROGRESS",
}

export enum MaxAllowedSocDateActionDispatchTypes {
  GET_MAX_ALLOWED_SOC_DATE_INVOKE = "GET_MAX_ALLOWED_SOC_DATE_INVOKE",
  GET_MAX_ALLOWED_SOC_DATE_SUCCESS = "GET_MAX_ALLOWED_SOC_DATE_SUCCESS",
  GET_MAX_ALLOWED_SOC_DATE_ERROR = "GET_MAX_ALLOWED_SOC_DATE_ERROR",
  GET_MAX_ALLOWED_SOC_DATE_RESET = "GET_MAX_ALLOWED_SOC_DATE_RESET",
}